import {
  Button,
  DateRangePicker,
  Input,
  InputNumber,
  Select,
  SingleDatePicker,
  TextArea,
} from '@prio365/prio365-react-library';
import { Form, notification } from 'antd';
import classNames from 'classnames';
import { toDate, isValidDate, formatDate, toISODateString } from '../incomingInvoiceDateUtils';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from '../../../../../../components/Flex';
import { ProjectId } from '../../../../../../models/Types';
import { makePrioStyles } from '../../../../../../theme/utils';
import { getCurrencySymbol } from '../../../../../../util/helpers';
import {
  apiOverrideInvoiceFile,
  apiTriggerOCRAnalysis,
  apiDuplicateCheckIncomingInvoice,
  DuplicateInvoiceSuggestion,
} from '../../../../api';
import FileDropZone from '../../FileDropZone';
import { useIncomingInvoiceDrawerContext } from '../incomingInvoiceComponents/IncomingInvoiceContextProvider';
import IncomingInvoiceDrawerSectionHeader from '../incomingInvoiceComponents/IncomingInvoiceDrawerSectionHeader';
import IncomingInvoiceNotRecognizedPill from '../incomingInvoiceComponents/IncomingInvoiceNotRecognizedPill';
import {
  EIncomingInvoiceState,
  IncomingInvoiceDto,
} from '../../../../../../models/IncomingInvoices';
import { sanitizeFileName } from '../../../../../../util/validateFileName';

const useStyles = makePrioStyles((theme) => ({
  root: {},
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr 1fr',
    rowGap: theme.spacing.regular,
    columnGap: theme.spacing.small,
  },
  formItem: {
    marginBottom: 0,
    transition: 'none',
  },
  fieldValue: {
    fontSize: theme.font.fontSize.small,
    color: theme.colors.application.typography.default,
  },
  colSpan2: {
    gridColumn: 'span 2',
  },
  colSpanFull: {
    gridColumn: '1 / -1',
  },
  duplicateAlert: {
    marginBottom: theme.spacing.regular,
    padding: theme.spacing.regular,
    backgroundColor: '#fffbe6',
    border: '1px solid #ffe58f',
    borderRadius: 4,
    display: 'flex',
    gap: theme.spacing.regular,
  },
  duplicateAlertIcon: {
    color: '#faad14',
    fontSize: 16,
    marginTop: 2,
    flexShrink: 0,
  },
  duplicateAlertContent: {
    flex: 1,
  },
  duplicateAlertTitle: {
    fontWeight: 600,
    fontSize: theme.font.fontSize.small,
    color: theme.colors.application.typography.default,
    marginBottom: 4,
  },
  duplicateAlertDescription: {
    fontSize: theme.font.fontSize.small,
    color: theme.colors.application.typography.default,
  },
  duplicateToggle: {
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing.small,
    fontSize: theme.font.fontSize.small,
    color: theme.colors.base.primary.default,
    marginTop: theme.spacing.small,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  duplicateList: {
    marginTop: theme.spacing.small,
  },
  duplicateItem: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr 1fr',
    gap: theme.spacing.small,
    padding: `${theme.spacing.small}px 0`,
    borderBottom: `1px solid ${theme.colors.application.border}`,
    fontSize: theme.font.fontSize.small,
  },
  duplicateItemHeader: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr 1fr',
    gap: theme.spacing.small,
    padding: `${theme.spacing.small}px 0`,
    borderBottom: `2px solid ${theme.colors.application.border}`,
    fontSize: theme.font.fontSize.small,
    fontWeight: 600,
  },
}));

export enum DataStepSectionCategory {
  GeneralData = 'generalData',
  Amounts = 'amounts',
  PerformancePeriod = 'performancePeriod',
  Notes = 'notes',
}

type SectionMode = Record<DataStepSectionCategory, 'view' | 'edit'>;

interface IncomingInvoicesDrawerDataStepProps {
  className?: string;
  projectId: ProjectId;
  invoice: IncomingInvoiceDto;
  hasFile: boolean;
  pdfLoading: boolean;
  loadInvoicePdf: () => void;
  onOCRTriggered?: () => void;
}

export const IncomingInvoicesDrawerDataStep: React.FC<
  IncomingInvoicesDrawerDataStepProps
> = ({
  className,
  projectId,
  invoice,
  hasFile,
  pdfLoading,
  loadInvoicePdf,
  onOCRTriggered,
}) => {
  //#region ------------------------------ Defaults
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const { form } = useIncomingInvoiceDrawerContext();

  const [sectionMode, setSectionMode] = useState<SectionMode>({
    [DataStepSectionCategory.GeneralData]: 'view',
    [DataStepSectionCategory.Amounts]: 'view',
    [DataStepSectionCategory.PerformancePeriod]: 'view',
    [DataStepSectionCategory.Notes]: 'view',
  });

  const currencyCode = form.getFieldValue('currency');
  const currencySymbol = useMemo(
    () => getCurrencySymbol(currencyCode, i18n.language),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currencyCode, i18n.language]
  );

  const [isUploading, setIsUploading] = useState(false);
  const [duplicates, setDuplicates] = useState<DuplicateInvoiceSuggestion[]>(
    []
  );
  const [duplicatesExpanded, setDuplicatesExpanded] = useState(false);
  //#endregion

  //#region ------------------------------ Methods / Handlers

  const formatMoney = (n: any) =>
    typeof n === 'number'
      ? n.toFixed(2)
      : typeof n === 'string'
        ? Number(n).toFixed(2)
        : '';

  const requiredStringRule = useMemo(
    () => ({
      required: true,
      validateTrigger: 'onBlur' as const,
      validator: (_: any, value: string) =>
        value && value.trim() !== '' ? Promise.resolve() : Promise.reject(),
      message: t(
        'accounting:incomingInvoices.fields.invoiceNumber.requiredText'
      ),
    }),
    [t]
  );

  const requiredNumberRule = useMemo(
    () => (message: string) => ({
      required: true,
      validateTrigger: 'onBlur' as const,
      message,
      validator: (_: any, value: number) =>
        typeof value === 'number' && !isNaN(value)
          ? Promise.resolve()
          : Promise.reject(),
    }),
    []
  );

  const nonZeroNumberRule = useMemo(
    () => (message: string) => ({
      validateTrigger: 'onBlur' as const,
      message,
      validator: (_: any, value: number) =>
        value === 0 ? Promise.reject() : Promise.resolve(),
    }),
    []
  );

  const requiredDateRule = useMemo(
    () => (message: string) => ({
      required: true,
      validateTrigger: 'onBlur' as const,
      message,
      validator: (_: any, value: any) =>
        isValidDate(value) ? Promise.resolve() : Promise.reject(),
    }),
    []
  );

  const updateTotals = (
    changed: Partial<{ totalExcludingVat: number; totalVat: number }>
  ) => {
    const net =
      changed.totalExcludingVat ??
      (Number(form.getFieldValue('totalExcludingVat')) || 0);
    const vat =
      changed.totalVat ?? (Number(form.getFieldValue('totalVat')) || 0);
    const sum = net + vat;
    form.setFieldsValue({
      ...changed,
      totalIncludingVat: sum,
      amountToTransfer: sum,
    });
  };

  const handleFileUpload = async (file: File) => {
    const cleanedFile = file.name
      ? new File([file], sanitizeFileName(file.name), { type: file.type })
      : file;
    setIsUploading(true);
    try {
      const result = await apiOverrideInvoiceFile(
        invoice.incomingInvoiceId,
        cleanedFile
      );
      if (result.result.status === 204 || result.result.ok) {
        loadInvoicePdf();
        notification.success({
          message: t('accounting:incomingInvoices.upload.uploadSuccess'),
          description: t(
            'accounting:incomingInvoices.upload.uploadToExistingSuccessDescription'
          ),
        });
      } else {
        throw new Error(`Upload failed with status: ${result.result.status}`);
      }
    } catch (e) {
      notification.error({
        message: t('accounting:incomingInvoices.upload.uploadFailed'),
        description: t(
          'accounting:incomingInvoices.upload.uploadFailedDescription'
        ),
      });
    } finally {
      setIsUploading(false);
    }
  };

  const triggerOCRAnalysis = async () => {
    const ocrResult = await apiTriggerOCRAnalysis(
      invoice.incomingInvoiceId,
      'german'
    );
    if (ocrResult.result.status === 204 || ocrResult.result.ok) {
      notification.success({
        message: t('accounting:incomingInvoices.upload.uploadSuccess'),
        description: t(
          'accounting:incomingInvoices.upload.uploadSuccessDescription'
        ),
      });
      onOCRTriggered?.();
    } else {
      throw new Error(
        `OCR analysis failed with status: ${ocrResult.result.status}`
      );
    }
  };
  //#endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    form.setFieldsValue({ currency: currencyCode });
  }, [form, currencyCode]);

  useEffect(() => {
    const fetchDuplicates = async () => {
      if (!invoice?.incomingInvoiceId) return;
      try {
        const response = await apiDuplicateCheckIncomingInvoice(
          invoice.incomingInvoiceId
        );
        if (response.data?.duplicateInvoicesSuggestion?.length) {
          setDuplicates(response.data.duplicateInvoicesSuggestion);
        }
      } catch {
        // silently ignore duplicate check errors
      }
    };
    fetchDuplicates();
  }, [invoice?.incomingInvoiceId]);
  //#endregion

  return (
    <Flex.Column className={classNames(classes.root, className)}>
      {duplicates.length > 0 && (
        <div className={classes.duplicateAlert}>
          <FontAwesomeIcon
            icon={['fal', 'triangle-exclamation']}
            className={classes.duplicateAlertIcon}
          />
          <div className={classes.duplicateAlertContent}>
            <div className={classes.duplicateAlertTitle}>
              {t(
                'accounting:incomingInvoices.drawerSteps.data.duplicateCheck.warning'
              )}
            </div>
            <div className={classes.duplicateAlertDescription}>
              {t(
                'accounting:incomingInvoices.drawerSteps.data.duplicateCheck.warningDescription',
                { count: duplicates.length }
              )}
            </div>
            <span
              className={classes.duplicateToggle}
              onClick={() => setDuplicatesExpanded((prev) => !prev)}
            >
              <FontAwesomeIcon
                icon={[
                  'fal',
                  duplicatesExpanded ? 'chevron-up' : 'chevron-down',
                ]}
              />
              {duplicatesExpanded
                ? t(
                    'accounting:incomingInvoices.drawerSteps.data.duplicateCheck.hideDetails'
                  )
                : t(
                    'accounting:incomingInvoices.drawerSteps.data.duplicateCheck.showDetails'
                  )}
            </span>
            {duplicatesExpanded && (
              <div className={classes.duplicateList}>
                <div className={classes.duplicateItemHeader}>
                  <span>
                    {t(
                      'accounting:incomingInvoices.drawerSteps.data.duplicateCheck.invoiceNumber'
                    )}
                  </span>
                  <span>
                    {t(
                      'accounting:incomingInvoices.drawerSteps.data.duplicateCheck.amount'
                    )}
                  </span>
                  <span>
                    {t(
                      'accounting:incomingInvoices.drawerSteps.data.duplicateCheck.createdAt'
                    )}
                  </span>
                  <span>
                    {t(
                      'accounting:incomingInvoices.drawerSteps.data.duplicateCheck.status'
                    )}
                  </span>
                </div>
                {duplicates.map((dup, index) => (
                  <div key={index} className={classes.duplicateItem}>
                    <span>{dup.invoiceNumber}</span>
                    <span>
                      {typeof dup.totalIncludingVat === 'number'
                        ? dup.totalIncludingVat.toFixed(2)
                        : ''}
                    </span>
                    <span>
                      {dup.createdAt ? formatDate(dup.createdAt) : ''}
                    </span>
                    <span>
                      {t(
                        `accounting:incomingInvoices.status.${dup.incomingInvoiceState}`,
                        { defaultValue: dup.incomingInvoiceState }
                      )}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {invoice?.incomingInvoiceState === EIncomingInvoiceState.MissingFile &&
        !pdfLoading && (
          <>
            {hasFile ? (
              <Button onClick={() => triggerOCRAnalysis()}>
                {t('accounting:incomingInvoices.upload.triggerOCRButton')}
              </Button>
            ) : (
              <FileDropZone
                onFileUpload={handleFileUpload}
                isUploading={isUploading}
                projectId={projectId}
                acceptedFileTypes={['.pdf', '.jpg', '.jpeg', '.png', '.tiff']}
                size="small"
                title={t(
                  'accounting:incomingInvoices.missingReceiptDropZoneText'
                )}
              />
            )}
          </>
        )}

      {/* Grunddaten */}
      <div>
        <IncomingInvoiceDrawerSectionHeader
          title={t(
            'accounting:incomingInvoices.drawerSteps.data.sectionTitles.invoiceData'
          )}
          mode={sectionMode[DataStepSectionCategory.GeneralData]}
          setMode={(m) =>
            setSectionMode((prev) => ({
              ...prev,
              [DataStepSectionCategory.GeneralData]: m,
            }))
          }
          iconProp={['fal', 'file-invoice']}
        />
        <div className={classes.formGrid}>
          <Form.Item
            name="invoiceNumber"
            label={t('accounting:incomingInvoices.fields.invoiceNumber.label')}
            rules={[requiredStringRule]}
            className={classNames(
              classes.formItem,
              sectionMode[DataStepSectionCategory.GeneralData] === 'edit' &&
                classes.colSpan2
            )}
          >
            {!form.getFieldValue('invoiceNumber') &&
            sectionMode[DataStepSectionCategory.GeneralData] === 'view' ? (
              <IncomingInvoiceNotRecognizedPill />
            ) : sectionMode[DataStepSectionCategory.GeneralData] === 'view' ? (
              <span className={classes.fieldValue}>
                {form.getFieldValue('invoiceNumber')}
              </span>
            ) : (
              <Input
                placeholder={t(
                  'accounting:incomingInvoices.fields.invoiceNumber.placeholder'
                )}
                full
                value={form.getFieldValue('invoiceNumber')}
                onChange={(val) => form.setFieldsValue({ invoiceNumber: val })}
              />
            )}
          </Form.Item>

          <Form.Item
            name="date"
            label={t('accounting:incomingInvoices.fields.date.label')}
            rules={[
              requiredDateRule(
                t('accounting:incomingInvoices.fields.date.requiredText')
              ),
            ]}
            getValueProps={(v) => ({
              value: v ? { dateValue: toDate(v), utsn: null } : undefined,
            })}
            getValueFromEvent={(d) =>
              d?.dateValue ? toISODateString(d.dateValue) : undefined
            }
            className={classNames(
              classes.formItem,
              sectionMode[DataStepSectionCategory.GeneralData] === 'edit' &&
                classes.colSpan2
            )}
          >
            {(!form.getFieldValue('date') ||
              !isValidDate(form.getFieldValue('date'))) &&
            sectionMode[DataStepSectionCategory.GeneralData] === 'view' ? (
              <IncomingInvoiceNotRecognizedPill />
            ) : sectionMode[DataStepSectionCategory.GeneralData] === 'view' ? (
              <span className={classes.fieldValue}>
                {formatDate(form.getFieldValue('date'))}
              </span>
            ) : (
              <SingleDatePicker />
            )}
          </Form.Item>
          <Form.Item
            name="dueDate"
            label={t('accounting:incomingInvoices.fields.dueDate.label')}
            rules={[]}
            getValueProps={(v) => ({
              value: v ? { dateValue: toDate(v), utsn: null } : undefined,
            })}
            getValueFromEvent={(d) =>
              d?.dateValue ? toISODateString(d.dateValue) : undefined
            }
            className={classNames(
              classes.formItem,
              sectionMode[DataStepSectionCategory.GeneralData] === 'edit' &&
                classes.colSpan2
            )}
          >
            {!form.getFieldValue('dueDate') &&
            sectionMode[DataStepSectionCategory.GeneralData] === 'view' ? (
              <IncomingInvoiceNotRecognizedPill />
            ) : sectionMode[DataStepSectionCategory.GeneralData] === 'view' ? (
              <span className={classes.fieldValue}>
                {formatDate(form.getFieldValue('dueDate'))}
              </span>
            ) : (
              <SingleDatePicker />
            )}
          </Form.Item>

          <Form.Item
            name="currency"
            label={t('accounting:incomingInvoices.fields.currency.label')}
            className={classNames(
              classes.formItem,
              sectionMode[DataStepSectionCategory.GeneralData] === 'edit' &&
                classes.colSpan2
            )}
          >
            {!form.getFieldValue('currency') &&
            sectionMode[DataStepSectionCategory.GeneralData] === 'view' ? (
              <IncomingInvoiceNotRecognizedPill />
            ) : sectionMode[DataStepSectionCategory.GeneralData] === 'view' ? (
              <span className={classes.fieldValue}>
                {form.getFieldValue('currency')}
              </span>
            ) : (
              <Select
                options={[
                  { value: 'EUR', label: 'Euro (EUR)' },
                  { value: 'USD', label: 'US-Dollar (USD)' },
                  { value: 'GBP', label: 'Britisches Pfund (GBP)' },
                ]}
                value={form.getFieldValue('currency')}
                onChange={(val) => form.setFieldsValue({ currency: val })}
                placeholder={t(
                  'accounting:incomingInvoices.fields.currency.placeholder'
                )}
              />
            )}
          </Form.Item>
        </div>
      </div>

      {/* Rechnungsbeträge */}
      <div>
        <IncomingInvoiceDrawerSectionHeader
          title={t(
            'accounting:incomingInvoices.drawerSteps.data.sectionTitles.amounts'
          )}
          mode={sectionMode[DataStepSectionCategory.Amounts]}
          setMode={(m) =>
            setSectionMode((prev) => ({
              ...prev,
              [DataStepSectionCategory.Amounts]: m,
            }))
          }
          iconProp={['fal', 'file-invoice-dollar']}
        />
        <div className={classes.formGrid}>
          <Form.Item
            name="totalExcludingVat"
            label={t(
              'accounting:incomingInvoices.fields.totalExcludingVat.label'
            )}
            rules={[
              requiredNumberRule(
                t(
                  'accounting:incomingInvoices.fields.totalExcludingVat.requiredText'
                )
              ),
              nonZeroNumberRule(
                t(
                  'accounting:incomingInvoices.fields.totalExcludingVat.notZeroText'
                )
              ),
            ]}
            className={classNames(
              classes.formItem,
              sectionMode[DataStepSectionCategory.Amounts] === 'edit' &&
                classes.colSpan2
            )}
          >
            {!form.getFieldValue('totalExcludingVat') &&
            form.getFieldValue('totalExcludingVat') !== 0 &&
            sectionMode[DataStepSectionCategory.Amounts] === 'view' ? (
              <IncomingInvoiceNotRecognizedPill />
            ) : sectionMode[DataStepSectionCategory.Amounts] === 'view' ? (
              <span className={classes.fieldValue}>
                {formatMoney(form.getFieldValue('totalExcludingVat'))}{' '}
                {currencySymbol}
              </span>
            ) : (
              <InputNumber
                suffixIcon={<>{currencySymbol}</>}
                precision={2}
                decimalSeparator=","
                full
                value={form.getFieldValue('totalExcludingVat')}
                onChange={(v) =>
                  updateTotals({
                    totalExcludingVat:
                      typeof v === 'string' ? Number(v) : (v as number),
                  })
                }
              />
            )}
          </Form.Item>

          <Form.Item
            name="totalVat"
            label={t('accounting:incomingInvoices.fields.totalVat.label')}
            rules={[]}
            className={classNames(
              classes.formItem,
              sectionMode[DataStepSectionCategory.Amounts] === 'edit' &&
                classes.colSpan2
            )}
          >
            {!form.getFieldValue('totalVat') &&
            form.getFieldValue('totalVat') !== 0 &&
            sectionMode[DataStepSectionCategory.Amounts] === 'view' ? (
              <IncomingInvoiceNotRecognizedPill />
            ) : sectionMode[DataStepSectionCategory.Amounts] === 'view' ? (
              <span className={classes.fieldValue}>
                {formatMoney(form.getFieldValue('totalVat'))} {currencySymbol}
              </span>
            ) : (
              <InputNumber
                suffixIcon={<>{currencySymbol}</>}
                precision={2}
                decimalSeparator=","
                full
                value={form.getFieldValue('totalVat')}
                onChange={(v) =>
                  updateTotals({
                    totalVat: typeof v === 'string' ? Number(v) : (v as number),
                  })
                }
              />
            )}
          </Form.Item>

          <Form.Item
            name="totalIncludingVat"
            label={t(
              'accounting:incomingInvoices.fields.totalIncludingVat.label'
            )}
            rules={[
              requiredNumberRule(
                t(
                  'accounting:incomingInvoices.fields.totalIncludingVat.requiredText'
                )
              ),
              nonZeroNumberRule(
                t(
                  'accounting:incomingInvoices.fields.totalIncludingVat.notZeroText'
                )
              ),
            ]}
            className={classNames(
              classes.formItem,
              sectionMode[DataStepSectionCategory.Amounts] === 'edit' &&
                classes.colSpan2
            )}
          >
            {!form.getFieldValue('totalIncludingVat') &&
            form.getFieldValue('totalIncludingVat') !== 0 &&
            sectionMode[DataStepSectionCategory.Amounts] === 'view' ? (
              <IncomingInvoiceNotRecognizedPill />
            ) : sectionMode[DataStepSectionCategory.Amounts] === 'view' ? (
              <span className={classes.fieldValue}>
                {formatMoney(form.getFieldValue('totalIncludingVat'))}{' '}
                {currencySymbol}
              </span>
            ) : (
              <InputNumber
                suffixIcon={<>{currencySymbol}</>}
                precision={2}
                decimalSeparator=","
                full
                value={form.getFieldValue('totalIncludingVat')}
                onChange={(v) =>
                  form.setFieldsValue({
                    totalIncludingVat:
                      typeof v === 'string' ? Number(v) : (v as number),
                  })
                }
              />
            )}
          </Form.Item>

          <Form.Item
            name="amountToTransfer"
            label={t(
              'accounting:incomingInvoices.fields.amountToTransfer.label'
            )}
            rules={[]}
            className={classNames(
              classes.formItem,
              sectionMode[DataStepSectionCategory.Amounts] === 'edit' &&
                classes.colSpan2
            )}
          >
            {!form.getFieldValue('amountToTransfer') &&
            form.getFieldValue('amountToTransfer') !== 0 &&
            sectionMode[DataStepSectionCategory.Amounts] === 'view' ? (
              <IncomingInvoiceNotRecognizedPill />
            ) : sectionMode[DataStepSectionCategory.Amounts] === 'view' ? (
              <span className={classes.fieldValue}>
                {formatMoney(form.getFieldValue('amountToTransfer'))}{' '}
                {currencySymbol}
              </span>
            ) : (
              <InputNumber
                suffixIcon={<>{currencySymbol}</>}
                precision={2}
                decimalSeparator=","
                full
                value={form.getFieldValue('amountToTransfer')}
                onChange={(v) =>
                  form.setFieldsValue({
                    amountToTransfer:
                      typeof v === 'string' ? Number(v) : (v as number),
                  })
                }
              />
            )}
          </Form.Item>
        </div>
      </div>

      {/* Leistungszeitraum */}
      <div>
        <IncomingInvoiceDrawerSectionHeader
          title={t(
            'accounting:incomingInvoices.drawerSteps.data.sectionTitles.performancePeriod'
          )}
          mode={sectionMode[DataStepSectionCategory.PerformancePeriod]}
          setMode={(m) =>
            setSectionMode((prev) => ({
              ...prev,
              [DataStepSectionCategory.PerformancePeriod]: m,
            }))
          }
          iconProp={['fal', 'calendar-days']}
        />
        <div className={classes.formGrid}>
          <Form.Item
            name="periodOfPerformanceFrom"
            label={t(
              'accounting:incomingInvoices.fields.performancePeriod.label'
            )}
            rules={[]}
            getValueProps={(from) => {
              const to = form.getFieldValue('periodOfPerformanceTo');
              return {
                value:
                  from && to
                    ? { dateValue: [toDate(from), toDate(to)], utsn: null }
                    : undefined,
              };
            }}
            getValueFromEvent={(nv) =>
              nv?.dateValue?.[0] ? toISODateString(nv.dateValue[0]) : undefined
            }
            className={classNames(
              classes.formItem,
              sectionMode[DataStepSectionCategory.PerformancePeriod] === 'edit'
                ? classes.colSpanFull
                : classes.colSpan2
            )}
          >
            {((!form.getFieldValue('periodOfPerformanceFrom') &&
              !form.getFieldValue('periodOfPerformanceTo')) ||
              (!isValidDate(form.getFieldValue('periodOfPerformanceFrom')) &&
                !isValidDate(form.getFieldValue('periodOfPerformanceTo')))) &&
            sectionMode[DataStepSectionCategory.PerformancePeriod] ===
              'view' ? (
              <IncomingInvoiceNotRecognizedPill />
            ) : sectionMode[DataStepSectionCategory.PerformancePeriod] ===
              'view' ? (
              <span className={classes.fieldValue}>
                {isValidDate(form.getFieldValue('periodOfPerformanceFrom')) &&
                isValidDate(form.getFieldValue('periodOfPerformanceTo'))
                  ? `${formatDate(form.getFieldValue('periodOfPerformanceFrom'))} - ${formatDate(
                      form.getFieldValue('periodOfPerformanceTo')
                    )}`
                  : ''}
              </span>
            ) : (
              <DateRangePicker
                placeholder={t(
                  'accounting:incomingInvoices.fields.performancePeriod.placeholder'
                )}
                onChange={(nv) => {
                  if (nv.dateValue && nv.dateValue.length === 2) {
                    const [startDate, endDate] = nv.dateValue;
                    form.setFieldsValue({
                      periodOfPerformanceFrom: startDate
                        ? toISODateString(startDate)
                        : undefined,
                      periodOfPerformanceTo: endDate
                        ? toISODateString(endDate)
                        : undefined,
                    });
                  }
                }}
              />
            )}
          </Form.Item>

          <Form.Item
            name="cashDiscountPercent"
            label={t(
              'accounting:incomingInvoices.fields.cashDiscountPercent.label'
            )}
            className={classNames(
              classes.formItem,
              sectionMode[DataStepSectionCategory.PerformancePeriod] ===
                'edit' && classes.colSpan2
            )}
          >
            {(form.getFieldValue('cashDiscountPercent') === undefined ||
              form.getFieldValue('cashDiscountPercent') === null ||
              form.getFieldValue('cashDiscountPercent') < 0) &&
            sectionMode[DataStepSectionCategory.PerformancePeriod] ===
              'view' ? (
              <IncomingInvoiceNotRecognizedPill />
            ) : sectionMode[DataStepSectionCategory.PerformancePeriod] ===
              'view' ? (
              <span className={classes.fieldValue}>
                {form.getFieldValue('cashDiscountPercent')}%
              </span>
            ) : (
              <InputNumber
                suffixIcon={<>%</>}
                decimalSeparator=","
                full
                min={0}
                max={100}
                value={form.getFieldValue('cashDiscountPercent')}
                onChange={(v) =>
                  form.setFieldsValue({
                    cashDiscountPercent:
                      typeof v === 'string' ? Number(v) : (v as number),
                  })
                }
              />
            )}
          </Form.Item>

          <Form.Item
            name="cashDiscountDays"
            label={t(
              'accounting:incomingInvoices.fields.cashDiscountDays.label'
            )}
            className={classNames(
              classes.formItem,
              sectionMode[DataStepSectionCategory.PerformancePeriod] ===
                'edit' && classes.colSpan2
            )}
          >
            {(form.getFieldValue('cashDiscountDays') === undefined ||
              form.getFieldValue('cashDiscountDays') === null ||
              form.getFieldValue('cashDiscountDays') < 0) &&
            sectionMode[DataStepSectionCategory.PerformancePeriod] ===
              'view' ? (
              <IncomingInvoiceNotRecognizedPill />
            ) : sectionMode[DataStepSectionCategory.PerformancePeriod] ===
              'view' ? (
              <span className={classes.fieldValue}>
                {form.getFieldValue('cashDiscountDays')}{' '}
                {t(
                  'accounting:incomingInvoices.drawerSteps.overview.fields.days'
                )}
              </span>
            ) : (
              <InputNumber
                min={0}
                max={365}
                decimalSeparator=","
                full
                value={form.getFieldValue('cashDiscountDays')}
                onChange={(v) =>
                  form.setFieldsValue({
                    cashDiscountDays:
                      typeof v === 'string' ? Number(v) : (v as number),
                  })
                }
                suffixIcon={
                  <>
                    {t(
                      'accounting:incomingInvoices.drawerSteps.overview.fields.days'
                    )}
                  </>
                }
              />
            )}
          </Form.Item>
        </div>
      </div>

      {/* Bemerkungen */}
      <div>
        <IncomingInvoiceDrawerSectionHeader
          title={t(
            'accounting:incomingInvoices.drawerSteps.data.sectionTitles.remarks'
          )}
          mode={sectionMode[DataStepSectionCategory.Notes]}
          setMode={(m) =>
            setSectionMode((prev) => ({
              ...prev,
              [DataStepSectionCategory.Notes]: m,
            }))
          }
          iconProp={['fal', 'comment']}
          disableEdit
        />
        <Form.Item
          name="notes"
          label={t('accounting:incomingInvoices.fields.remarks.label')}
          className={classNames(classes.formItem, classes.colSpanFull)}
        >
          <TextArea
            placeholder={t(
              'accounting:incomingInvoices.fields.remarks.placeholder'
            )}
            autoSize={{ minRows: 3, maxRows: 6 }}
            value={form.getFieldValue('notes')}
            onChange={(val) => form.setFieldsValue({ notes: val })}
          />
        </Form.Item>
      </div>
    </Flex.Column>
  );
};

export default IncomingInvoicesDrawerDataStep;
